import React from 'react';

/**
 * The replace-logo state machine: the brand's live logo family, and the runs in flight
 * against it.
 *
 * This replaces the browser-local override store the page used before there was an API.
 * The difference is not just where the artwork lives — it is WHEN it exists. A local
 * replacement was instant: write it, render it. A real one is a pipeline run, so the flow
 * has a middle:
 *
 *   idle ──replace()──► uploading ──► queued ──► running ──► idle (family refetched)
 *                                                    │
 *                                                    └──► failed (family unchanged)
 *
 * Between `queued` and the refetch the page keeps showing the CURRENT logo, because that is
 * what the site is still serving. Only when the run settles does the new artwork appear, and
 * it appears for every slot the run touched — not just the one the owner picked, since a
 * cascade rebuilds everything derived from it.
 *
 * A queued run is written to `localStorage` so it OUTLIVES the page: reloading, navigating
 * away and back, or closing the tab all pick the same run up again rather than losing it
 * (see the storage helpers below).
 *
 * WHY POLLING. The pipeline reports progress to the owner-hub backend by callback, and the
 * brand record eventually carries the result. Polling the run directly is narrower and does
 * not wait on that record to catch up: ask casino-creation whether this pipeline is over,
 * then read the family once. See `./logo-api`.
 */
import {
  fetchLogoFamily,
  fetchLogoGraph,
  fetchRunStatus,
  logoApiConfigured,
  queueLogoRun,
  runAffects,
  uploadArtwork,
  type LogoFamily,
  type LogoGraph,
} from './logo-api';

/** How often to ask whether the run is over. */
const POLL_INTERVAL_MS = 4000;
/**
 * When to stop asking. A cascade costs a couple of image generations, so minutes is normal
 * and this ceiling is deliberately generous — it exists to stop an abandoned poll running
 * forever, not to predict how long a run takes. Hitting it leaves the logos as they are and
 * tells the owner to check back, which is true: the run itself is unaffected.
 */
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

/** What the owner asked for, alongside the file. Mirrors the two toggles in the modal. */
export type ReplacementOptions = {
  /** Let Lumio clean up / refit the artwork, rather than storing the file as supplied. */
  prepareUpload: boolean;
  /** Rebuild every logo derived from this one. */
  cascade: boolean;
};

/** A replacement the owner has started but that has not landed yet. */
export type PendingReplacement = {
  slot: string;
  /** Every slot this run will rewrite — the whole cascade, not just the picked one. */
  affects: string[];
  phase: 'uploading' | 'running';
};

/* ── surviving a reload ──────────────────────────────────────────────────────
 * A queued run outlives the page that started it: the pipeline keeps going server-side
 * whatever the browser does. Held in component state alone, a refresh (or a trip to
 * another page and back, or closing the tab) lost the poll AND the fact that anything was
 * happening — the page came back showing settled logos with Replace re-enabled on artwork
 * that was about to change underneath the owner, and the only thing that would have told
 * them otherwise was casino-creation refusing their second run.
 *
 * So the run is written down at the moment it is queued and picked up again on mount.
 * `localStorage` rather than `sessionStorage` because a run takes minutes and an owner who
 * closes the tab and comes back should still find it. Keyed by tenant, like everything
 * else here, so one casino's run is never attributed to another.
 */
const RUN_STORAGE_PREFIX = 'ows.logo-run.';

/** The queued run, as written down. `startedAt` is what bounds a resumed poll. */
export type StoredRun = { pipelineId: string; slot: string; affects: string[]; startedAt: number };

const runKey = (tenantId: number): string => `${RUN_STORAGE_PREFIX}${tenantId}`;

/**
 * Read one stored run out of its serialised form, rejecting anything that is not a complete
 * record. Storage is shared, long-lived and writable by any past version of this code, so a
 * half-written or outdated entry has to read as "nothing stored" rather than resume a poll
 * against a pipeline id that isn't one.
 */
export const parseStoredRun = (raw: string | null): StoredRun | undefined => {
  if (!raw) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (typeof parsed !== 'object' || parsed === null) return undefined;
  const { pipelineId, slot, affects, startedAt } = parsed as Partial<StoredRun>;
  // Shape-checked, not merely typed: this value is interpolated into a proxied request PATH
  // that carries the owner's token, and the path builder escapes reserved characters but not
  // `..` traversal. Storage is writable by anything else running on this origin, so the
  // parser that already owns "reject untrusted records" is the right place to bound it.
  if (typeof pipelineId !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(pipelineId)) return undefined;
  if (typeof slot !== 'string' || slot === '') return undefined;
  if (typeof startedAt !== 'number' || !Number.isFinite(startedAt)) return undefined;
  if (!Array.isArray(affects)) return undefined;
  return { pipelineId, slot, affects: affects.filter((key): key is string => typeof key === 'string'), startedAt };
};

/**
 * Whether a stored run is still worth picking up. Past the ceiling the record is written
 * off: resuming it could only ever end in the timeout failure, and a run left behind by a
 * tab closed days ago must not come back as "Updating…" on a page that is fully settled.
 *
 * Deliberately measured from when the run STARTED, not from this mount — otherwise each
 * reload would hand an abandoned run a fresh ceiling and it would never expire.
 */
export const isRunResumable = (run: StoredRun | undefined, now: number): boolean =>
  run !== undefined && now < run.startedAt + POLL_TIMEOUT_MS;

/** Reads back a stored run, treating anything malformed or unavailable as nothing stored. */
export const readStoredRun = (tenantId: number): StoredRun | undefined => {
  try {
    return parseStoredRun(window.localStorage.getItem(runKey(tenantId)));
  } catch {
    // Storage unavailable (private mode) — behave as if nothing was ever stored.
    return undefined;
  }
};

const writeStoredRun = (tenantId: number, run: StoredRun): void => {
  try {
    window.localStorage.setItem(runKey(tenantId), JSON.stringify(run));
  } catch {
    // Storage unavailable. The run still works, it just won't survive a reload.
  }
};

const clearStoredRun = (tenantId: number): void => {
  try {
    window.localStorage.removeItem(runKey(tenantId));
  } catch {
    // Storage unavailable — nothing was written, so nothing to remove.
  }
};

/**
 * Why a replacement stopped. A CLOSED set, deliberately: `reason` used to be a free-form
 * string (raw axios messages included) while the one surface that reports these branches on
 * specific values, so every reason nobody had thought to handle was silently dropped and the
 * owner was told nothing at all.
 *
 *  - `queue`  — never started. Reported by the DIALOG, which is still open; the page stays
 *               quiet so the owner is not told twice.
 *  - `run`    — the pipeline ended without replacing the artwork.
 *  - `lost`   — we stopped waiting (the polling ceiling). The run is very likely still going.
 *  - `refresh`— the run ended but the family could not be read back, so what is on screen may
 *               no longer be what the site serves.
 */
export type ReplacementFailureReason = 'queue' | 'run' | 'lost' | 'refresh';

export type ReplacementFailure = {
  slot: string;
  reason: ReplacementFailureReason;
  /** Underlying error text. Diagnostics only — never shown to the owner. */
  detail?: string;
};

export type LogoReplacementState = {
  /** The live family, or undefined until it has loaded (or if it could not be read). */
  family: LogoFamily | undefined;
  /** The dependency graph, fetched once. Undefined until loaded. */
  graph: LogoGraph | undefined;
  /** True from the first render until a read of the CURRENT brand's family has answered (with a family or a failure). */
  loading: boolean;
  /** That read failed and no family has been read since: an error with a retry, never "no logos". */
  failed: boolean;
  /** Read the family again (the error's retry). */
  reload: () => void;
  /** The run currently in flight, if any. Only one run per brand can be queued at a time. */
  pending: PendingReplacement | undefined;
  /** The last failure, cleared when a new replacement starts. */
  failure: ReplacementFailure | undefined;
  /**
   * Start a replacement. Resolves once the run is QUEUED — the point at which the dialog
   * can close and the owner is free to leave — not when the new artwork exists. The rest
   * of the run is followed in the background and reported through `pending` and `family`.
   * Never rejects: a failure resolves false and lands in `failure`.
   */
  replace: (slot: string, imageDataUri: string, options: ReplacementOptions) => Promise<boolean>;
};

const sleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    // Cleanup runs BOTH ways. `{ once: true }` only unregisters a listener that actually
    // fires, so the timer path used to leave its listener behind — and a run polled to the
    // ceiling calls this ~150 times against one mount-scoped signal, so a brand switch then
    // ran 150 stale handlers, each clearing an already-fired timer and rejecting an
    // already-settled promise.
    let timer = 0;
    const onAbort = (): void => {
      window.clearTimeout(timer);
      reject(new Error('aborted'));
    };
    timer = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });

/**
 * How the tracking of one run ended.
 *
 *  - `settled` / `failed` — the pipeline reached a terminal state and we saw it.
 *  - `timeout` — the ceiling was hit. This says nothing about the run, which is very likely
 *    still going; it only means we stopped asking.
 */
type RunOutcome = 'settled' | 'failed' | 'timeout';

/**
 * Poll one run until it is over.
 *
 * `deadline` is absolute rather than "now + the ceiling" so a run picked up after a reload
 * is still bounded by when it STARTED — otherwise each reload would hand it a fresh ten
 * minutes and an abandoned run could be polled indefinitely.
 *
 * A failed status REQUEST is not an outcome. A run is polled well over a hundred times
 * through the proxy, so a 502 or a dropped connection somewhere in that window is ordinary,
 * and the endpoint being briefly unreachable says nothing about the pipeline. Earlier
 * versions of this loop drew a conclusion from it twice, and both were wrong: ending the
 * run on the first error abandoned live runs, and giving up after N errors created a state
 * with no exit — nothing was polling, yet `pending` stayed set, so every Replace on the page
 * was refused until a full reload. So errors are simply skipped; the deadline above is what
 * bounds this loop, and it bounds it whether the requests succeed or not.
 */
const waitForRun = async (
  pipelineId: string,
  tenantId: number,
  deadline: number,
  signal: AbortSignal,
): Promise<RunOutcome> => {
  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS, signal);
    let status;
    try {
      status = await fetchRunStatus(pipelineId, tenantId, signal);
    } catch (error) {
      // An aborted request is the brand switching or the page unmounting, not a bad poll.
      if (signal.aborted) throw error;
      continue;
    }
    if (status.failed) return 'failed';
    if (status.settled) return 'settled';
  }
  return 'timeout';
};

/**
 * The active brand's logo family and the operations on it.
 *
 * `tenantId` keys everything: switching brands drops the previous brand's family, graph
 * request and any in-flight poll, so one casino's replacement can never be attributed to
 * another. The graph is tenant-independent but still refetched per brand mount — it is one
 * small cached call, and threading a module-level cache through a component that can render
 * without a brand costs more than it saves.
 */
export const useLogoReplacement = (tenantId: number | undefined, brandName: string): LogoReplacementState => {
  const [family, setFamily] = React.useState<LogoFamily | undefined>(undefined);
  const [graph, setGraph] = React.useState<LogoGraph | undefined>(undefined);
  // How the latest family read ended, stamped with the brand it was made for. `loading` is derived from it rather than
  // set by the read, so it holds from the very first render of a brand (and of a newly selected one) until a read of
  // THAT brand has answered: no frame reports "loaded, no family" before the read is in.
  const [familyRead, setFamilyRead] = React.useState<{ tenantId: number; failed: boolean } | undefined>(undefined);
  const reads = Boolean(tenantId) && logoApiConfigured();
  const loading = reads && familyRead?.tenantId !== tenantId;
  const failed = reads && !loading && familyRead?.failed === true && family === undefined;
  const [pending, setPending] = React.useState<PendingReplacement | undefined>(undefined);
  const [failure, setFailure] = React.useState<ReplacementFailure | undefined>(undefined);

  // One controller per mounted brand: aborts the initial reads AND any run poll when the
  // brand changes or the page unmounts, so nothing writes state for a brand no longer shown.
  const abortRef = React.useRef<AbortController | undefined>(undefined);
  // `pending` mirrored for `replace` to read without taking it as a dependency (which would
  // rebuild the callback on every phase change and hand the dialog a stale one mid-flight).
  const pendingRef = React.useRef<PendingReplacement | undefined>(undefined);
  pendingRef.current = pending;

  /**
   * Follow one queued run to its end, wherever it was started — by `replace` just now, or by
   * a page that has since been reloaded away. Reads the family back either way: a run can
   * fail AFTER replacing some slots, and the stored family is the only honest account of
   * what the site now serves.
   *
   * An ABORT (brand switch, unmount) deliberately leaves the stored run in place: the
   * pipeline is still going, and the next mount should pick it up rather than forget it.
   * Only a run that has actually ended is written off.
   */
  const follow = React.useCallback(
    async (run: StoredRun, tenant: number, controller: AbortController): Promise<void> => {
      try {
        const outcome = await waitForRun(run.pipelineId, tenant, run.startedAt + POLL_TIMEOUT_MS, controller.signal);

        const next = await fetchLogoFamily(tenant, controller.signal);
        if (controller.signal.aborted) return;
        clearStoredRun(tenant);
        setFamily(next);
        setPending(undefined);
        // `timeout` is NOT a failed replacement — the ceiling exists to stop an abandoned
        // poll, not to predict how long a run takes, and this module's own POLL_TIMEOUT_MS
        // doc promises the run is unaffected. Reporting it as "couldn't be updated" told the
        // owner their artwork was unchanged while the pipeline was still writing it, and sent
        // them to re-upload into a refusal.
        if (outcome === 'failed') setFailure({ slot: run.slot, reason: 'run' });
        else if (outcome === 'timeout') setFailure({ slot: run.slot, reason: 'lost' });
      } catch (error) {
        if (controller.signal.aborted) return;
        // The run reached a terminal state (we are past `waitForRun`); only reading the family
        // back failed, so the artwork on the site may well have changed while what is on
        // screen has not. `refresh` says exactly that — it is NOT a failed replacement, and it
        // is not the queue-time refusal the dialog reports either.
        clearStoredRun(tenant);
        setPending(undefined);
        setFailure({
          slot: run.slot,
          reason: 'refresh',
          detail: error instanceof Error ? error.message : undefined,
        });
      }
    },
    [],
  );

  /**
   * Read the brand's family (and the graph beside it) under the brand's controller. The family is what the page
   * renders, so how its read ended is recorded either way: a failure is the page's error with a retry, never "no
   * logos". The graph only drives which questions the replace modal asks; losing it degrades that modal, not the page,
   * so it is settled independently and its failure is swallowed.
   */
  const readFamily = React.useCallback((tenant: number, controller: AbortController): void => {
    void (async () => {
      const [familyResult, graphResult] = await Promise.allSettled([
        fetchLogoFamily(tenant, controller.signal),
        fetchLogoGraph(tenant, controller.signal),
      ]);
      if (controller.signal.aborted) return;
      if (familyResult.status === 'fulfilled') setFamily(familyResult.value);
      if (graphResult.status === 'fulfilled') setGraph(graphResult.value);
      setFamilyRead({ tenantId: tenant, failed: familyResult.status === 'rejected' });
    })();
  }, []);

  React.useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setFamily(undefined);
    // Dropped with the family it describes: a brand selected again (A, B, then A) is read again, and until that read
    // answers it is loading, not "answered" by the read made the first time round.
    setFamilyRead(undefined);
    setGraph(undefined);
    setPending(undefined);
    setFailure(undefined);

    if (!tenantId || !logoApiConfigured()) return () => controller.abort();

    // Pick up a run this brand already had going. Restored BEFORE the family loads, so the
    // page comes back already showing the replacement in progress instead of offering
    // Replace on artwork that is about to change. Past the ceiling it is written off: the
    // record is stale, and re-polling it would only ever end in the timeout failure.
    const stored = readStoredRun(tenantId);
    if (isRunResumable(stored, Date.now())) {
      const run = stored as StoredRun;
      setPending({ slot: run.slot, affects: run.affects, phase: 'running' });
      void follow(run, tenantId, controller);
    } else if (stored) {
      clearStoredRun(tenantId);
    }

    readFamily(tenantId, controller);

    return () => controller.abort();
  }, [tenantId, follow, readFamily]);

  // The error's retry: reads this brand's family again, the section back on its skeleton until it answers.
  const reload = React.useCallback((): void => {
    const controller = abortRef.current;
    if (!tenantId || !controller || controller.signal.aborted) return;
    setFamilyRead(undefined);
    readFamily(tenantId, controller);
  }, [tenantId, readFamily]);

  const replace = React.useCallback(
    async (slot: string, imageDataUri: string, options: ReplacementOptions): Promise<boolean> => {
      const controller = abortRef.current;
      if (!tenantId || !controller) return false;
      // Only one run per brand can be queued, and casino-creation refuses the second — but
      // the refusal used to arrive AFTER this function had already overwritten `pending` with
      // the new slot and then cleared it, so the live run lost its "Updating…" labels and its
      // Replace lock and finished minutes later with nothing on screen saying so. Refuse here
      // instead: the dialog shows the same "something else is still being updated" reason,
      // and the run in flight keeps its state.
      if (pendingRef.current) return false;

      // What this run will rewrite, which is what the page shows as in progress: the favicon
      // included whenever the run redraws it, and the whole family for a rebrand (see `runAffects`).
      const affects = runAffects(graph, slot, options.cascade);

      setFailure(undefined);
      setPending({ slot, affects, phase: 'uploading' });

      let run;
      try {
        const uploadUrl = await uploadArtwork(imageDataUri, brandName, controller.signal);
        run = await queueLogoRun({ tenantId, slot, uploadUrl, ...options }, controller.signal);
      } catch (error) {
        if (controller.signal.aborted) return false;
        setPending(undefined);
        // Nothing has changed: the artwork never reached a run. Either the upload failed, or
        // casino-creation refused to queue — most often because another run is already in
        // flight for this brand (two runs would each rewrite the whole family, so the second
        // one's work would be silently lost).
        setFailure({ slot, reason: 'queue', detail: error instanceof Error ? error.message : undefined });
        return false;
      }

      // Queued. Written down BEFORE the dialog is told it may close, so the run is
      // recoverable from the first moment it exists rather than from whenever this page
      // next happens to render.
      const record: StoredRun = { pipelineId: run.pipelineId, slot, affects, startedAt: Date.now() };
      writeStoredRun(tenantId, record);

      // The brand may have been switched while the upload was in flight. The run is real and
      // stays written down (the next mount of THIS tenant resumes it), but none of it may be
      // published into state any more: slot ids are shared across brands, so writing `pending`
      // here would pin the newly selected casino's tiles to "Updating…" — and `follow` would
      // then be handed an already-aborted controller, whose catch returns without ever
      // clearing it. Every other write in this hook is guarded; this one was not.
      if (controller.signal.aborted) return true;
      setPending({ slot, affects, phase: 'running' });
      void follow(record, tenantId, controller);
      return true;
    },
    [tenantId, brandName, graph, follow],
  );

  return { family, graph, loading, failed, reload, pending, failure, replace };
};
